import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, FileText, Eye, Edit, Trash2, Clock } from 'lucide-react';
import { useFirebase } from '../contexts/FirebaseContext';
import { useDialog } from '../components/DialogProvider';
import { SkeletonTable } from './Skeleton';

const OnlineTestManagement = () => {
  const navigate = useNavigate();
  const { getOnlineTests, addOnlineTest, deleteTest } = useFirebase();
  const dialog = useDialog();
  const hasShownExpiredNotification = useRef(false);

  const [allTests, setAllTests] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('current'); // 'current' or 'old'
  const [quickFilter, setQuickFilter] = useState(null); // 'today', 'tomorrow'
  const [dateRange, setDateRange] = useState({ from: '', to: '' });

  // Form modal state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    testName: '',
    startDate: '',
    endDate: '',
    testStartHour: '10',
    testStartMinute: '00',
    testStartPeriod: 'AM',
    testCloseHour: '11',
    testCloseMinute: '00',
    testClosePeriod: 'AM',
    duration: 60,
    totalMarks: '',
    questionIds: []
  });

  const hours = Array.from({ length: 12 }, (_, i) => (i + 1).toString().padStart(2, '0'));
  const minutes = Array.from({ length: 12 }, (_, i) => (i * 5).toString().padStart(2, '0'));

  // Helper function to format dates for URL parameters
  const formatDateForUrl = (dateValue) => {
    if (!dateValue) return ''
    try {
      let date
      if (dateValue instanceof Date) {
        date = dateValue
      } else if (dateValue.toDate && typeof dateValue.toDate === 'function') {
        date = dateValue.toDate()
      } else {
        date = new Date(dateValue)
      }

      if (isNaN(date.getTime())) return ''

      // Format as YYYY-MM-DD for HTML date input
      return date.toISOString().split('T')[0]
    } catch (error) {
      console.error('Error formatting date for URL:', error)
      return ''
    }
  }

  // Safely construct a Date from date and time strings (supports yyyy-mm-dd and dd-mm-yyyy)
  const makeDateFromParts = (dateStr, timeStr) => {
    if (!dateStr) return new Date('invalid');
    let y, m, d;
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      // yyyy-mm-dd (HTML date value)
      [y, m, d] = dateStr.split('-').map((v) => parseInt(v, 10));
    } else if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) {
      // dd-mm-yyyy (placeholder/localized)
      const [dd, mm, yyyy] = dateStr.split('-').map((v) => parseInt(v, 10));
      y = yyyy; m = mm; d = dd;
    } else {
      // Fallback to native parsing
      return new Date(dateStr);
    }

    let hours = 0, minutes = 0;
    if (timeStr && /^\d{2}:\d{2}(:\d{2})?$/.test(timeStr)) {
      const parts = timeStr.split(':').map((v) => parseInt(v, 10));
      hours = parts[0] || 0; minutes = parts[1] || 0;
    }
    // Months are 0-based
    return new Date(y, (m || 1) - 1, d || 1, hours, minutes, 0, 0);
  };

  // Compute duration in minutes based ONLY on startTime and endTime (same day only)
  const computeDurationMinutes = (data) => {
    try {
      const { startTime, endTime } = data;
      if (!startTime || !endTime) return NaN;

      const parseToMinutes = (t) => {
        if (!/^\d{1,2}:\d{2}(\s?(AM|PM))?$/i.test(t)) return NaN;

        // Handle both 12-hour and 24-hour formats
        let [time, period] = t.split(/\s+/);
        let [hours, minutes] = time.split(':').map(num => parseInt(num, 10));

        // Convert 12-hour to 24-hour format if period exists
        if (period) {
          period = period.toUpperCase();
          if (period === 'PM' && hours !== 12) {
            hours += 12;
          } else if (period === 'AM' && hours === 12) {
            hours = 0;
          }
        }

        return (hours * 60) + (minutes || 0);
      };

      const startMin = parseToMinutes(startTime);
      const endMin = parseToMinutes(endTime);
      if (isNaN(startMin) || isNaN(endMin)) return NaN;

      // Only calculate difference within same day - no cross-day calculation
      if (endMin <= startMin) {
        return NaN; // Invalid: end time must be after start time on same day
      }

      return endMin - startMin;
    } catch (err) {
      return NaN;
    }
  };

  useEffect(() => {
    loadOnlineTests();
  }, []);

  const loadOnlineTests = async () => {
    try {
      setLoading(true);

      // Load online tests from /onlineTests collection
      const onlineTestsData = await getOnlineTests();
      setAllTests(onlineTestsData);

    } catch (error) {
      console.error('Error loading online tests:', error);
      setAllTests([]);
    } finally {
      setLoading(false);
    }
  };



  // Helper function to check if a test is old (end date has passed)
  const isTestOld = (test) => {
    if (!test.endDate) return false;
    try {
      const endDate = test.endDate.toDate ? test.endDate.toDate() : new Date(test.endDate);
      const now = new Date();
      // rigorous comparison using full timestamp
      return endDate.getTime() < now.getTime();
    } catch (error) {
      return false;
    }
  };

  const filteredTests = allTests
    .filter(test =>
      test.testName.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .filter(test => {
      // Filter based on active tab
      if (activeTab === 'current') {
        return !isTestOld(test); // Show only current/future tests
      } else {
        return isTestOld(test); // Show only old tests
      }
    })
    .filter(test => {
      // Filter based on quick filters / date range
      if (activeTab === 'old') return true; // Don't apply date filters to old tab by default or let it be? User said "todays and onword", implying current.

      if (!test.startDate) return true;

      const testDate = test.startDate.toDate ? test.startDate.toDate() : new Date(test.startDate);
      const testDateStr = testDate.toISOString().split('T')[0];

      const today = new Date();
      const todayStr = today.toISOString().split('T')[0];

      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tomorrowStr = tomorrow.toISOString().split('T')[0];

      if (quickFilter === 'today') {
        return testDateStr === todayStr;
      }
      if (quickFilter === 'tomorrow') {
        return testDateStr === tomorrowStr;
      }
      if (dateRange.from || dateRange.to) {
        if (dateRange.from && testDateStr < dateRange.from) return false;
        if (dateRange.to && testDateStr > dateRange.to) return false;
        return true;
      }
      return true;
    })
    .sort((a, b) => {
      const getMillis = (d) => {
        if (!d) return 0;
        // Firestore Timestamp
        if (d.toDate && typeof d.toDate === 'function') return d.toDate().getTime();
        // Native Date
        if (d instanceof Date) return d.getTime();
        // Strings
        if (typeof d === 'string') {
          // Try standard parsing
          let date = new Date(d);
          if (!isNaN(date.getTime())) return date.getTime();

          // Try parsing dd/mm/yyyy or dd-mm-yyyy
          const parts = d.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})/);
          if (parts) {
            // parts[1] is day, parts[2] is month, parts[3] is year
            date = new Date(parts[3], parts[2] - 1, parts[1]);
            if (!isNaN(date.getTime())) return date.getTime();
          }
        }
        return 0;
      };

      if (activeTab === 'old') {
        // Sort by End Date Descending (Most recently expired first)
        const timeA = getMillis(a.endDate || a.startDate);
        const timeB = getMillis(b.endDate || b.startDate);
        return timeB - timeA;
      } else {
        // Sort Ascending (Earliest start date first for upcoming/current)
        const timeA = getMillis(a.startDate);
        const timeB = getMillis(b.startDate);
        return timeA - timeB;
      }
    });

  const handleCreateNewTest = () => {
    setShowCreateForm(true);
  };

  // Helper function to parse duration from input
  const parseDuration = (durationValue) => {
    const num = parseInt(durationValue);
    return isNaN(num) ? 60 : num;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!formData.testName.trim() || !formData.startDate || !formData.endDate) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (!formData.duration) {
      toast.error('Please enter test duration');
      return;
    }

    if (!formData.testStartHour || !formData.testCloseHour) {
      toast.error('Please enter test start time and close time');
      return;
    }

    try {
      setLoading(true);

      // Convert 12-hour values to 24-hour for the Date object
      // Convert 12-hour values to 24-hour for the Date object
      const get24HourTime = (h, m, p) => {
        let hour = parseInt(h);
        if (p === 'PM' && hour !== 12) hour += 12;
        if (p === 'AM' && hour === 12) hour = 0;
        return `${hour.toString().padStart(2, '0')}:${m}`;
      };

      const start24 = get24HourTime(formData.testStartHour, formData.testStartMinute, formData.testStartPeriod);
      const close24 = get24HourTime(formData.testCloseHour, formData.testCloseMinute, formData.testClosePeriod);

      // Create start datetime
      const startDateTime = new Date(formData.startDate + 'T' + start24);

      // Create end datetime
      const endDateTime = new Date(formData.endDate + 'T' + close24);

      // Validate that end datetime is after start datetime
      if (endDateTime <= startDateTime) {
        toast.error('Test close time must be after test start time');
        setLoading(false);
        return;
      }

      // Parse duration from text input
      const durationInMinutes = parseDuration(formData.duration);

      // Create online test data matching exact Firestore structure
      const now = new Date();
      const testData = {
        testName: formData.testName,
        startDate: startDateTime,
        endDate: endDateTime,
        testStartTime: `${formData.testStartHour}:${formData.testStartMinute} ${formData.testStartPeriod}`,
        testCloseTime: `${formData.testCloseHour}:${formData.testCloseMinute} ${formData.testClosePeriod}`,
        duration: durationInMinutes,
        totalMarks: formData.totalMarks || "",
        totalQuestions: 0,
        questionIds: [],
        createdAt: now,
        updatedAt: now
      };

      // Add online test to Firestore with toast feedback
      const creating = addOnlineTest(testData)
      await toast.promise(creating, {
        loading: 'Creating online test...',
        success: `Online test "${formData.testName}" created successfully!`,
        error: (e) => `Failed to create test: ${e?.message || 'Unknown error'}`
      })

      // Close the form and reset
      handleCloseForm();

      // Reload tests to show the new one
      await loadOnlineTests();
      // Optional guidance toast
      toast.success('You can now add questions to this online test.');
    } catch (error) {
      console.error('❌ Error creating online test:', error);
      toast.error(`Failed to create test: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseForm = () => {
    setShowCreateForm(false);
    setFormData({
      testName: '',
      startDate: '',
      endDate: '',
      testStartHour: '10',
      testStartMinute: '00',
      testStartPeriod: 'AM',
      testCloseHour: '11',
      testCloseMinute: '00',
      testClosePeriod: 'AM',
      duration: 60,
      totalMarks: '',
      questionIds: []
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Auto-calculate duration
  useEffect(() => {
    const { startDate, endDate, testStartHour, testStartMinute, testStartPeriod, testCloseHour, testCloseMinute, testClosePeriod } = formData;

    if (startDate && endDate && testStartHour && testStartMinute && testCloseHour && testCloseMinute) {
      const get24Hour = (h, m, p) => {
        let hour = parseInt(h);
        const minute = parseInt(m);
        if (p === 'PM' && hour !== 12) hour += 12;
        if (p === 'AM' && hour === 12) hour = 0;
        return { hour, minute };
      };

      const start = get24Hour(testStartHour, testStartMinute, testStartPeriod);
      const close = get24Hour(testCloseHour, testCloseMinute, testClosePeriod);

      const startDt = new Date(startDate);
      startDt.setHours(start.hour, start.minute, 0, 0);

      const endDt = new Date(endDate);
      endDt.setHours(close.hour, close.minute, 0, 0);

      const diffMs = endDt.getTime() - startDt.getTime();
      const diffMins = Math.floor(diffMs / 60000);

      if (diffMins > 0) {
        setFormData(prev => ({ ...prev, duration: diffMins }));
      }
    }
  }, [formData.startDate, formData.endDate, formData.testStartHour, formData.testStartMinute, formData.testStartPeriod, formData.testCloseHour, formData.testCloseMinute, formData.testClosePeriod]);

  const handleDeleteTest = async (test) => {
    const confirmMessage = `Are you sure you want to delete "${test.testName}" ? This will permanently delete the test and all associated questions.`;
    const ok = await dialog.confirm({ title: 'Delete Test', message: confirmMessage, confirmText: 'Yes, Delete', tone: 'danger' });
    if (ok) {
      try {
        setLoading(true);
        console.log('🗑️ Deleting online test:', test);

        // Delete the test from Firestore /onlineTests collection
        await deleteTest(test.id);

        // Reload tests to reflect the deletion
        await loadOnlineTests();

        toast.success(`Online test "${test.testName}" has been deleted successfully.`);
      } catch (error) {
        console.error('❌ Error deleting online test:', error);
        toast.error(`Failed to delete test: ${error.message}`);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <FileText className="w-8 h-8 text-green-600" />
            <h1 className="text-2xl font-bold text-gray-900">Online Tests Management</h1>
          </div>
          <p className="text-gray-600">
            Manage all online tests across all content
          </p>
        </div>
        <div className="flex gap-3 w-full md:w-auto">
          <button
            onClick={handleCreateNewTest}
            className="w-full md:w-auto bg-green-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Create New Test
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 mb-6 border-b border-gray-200 overflow-x-auto pb-1">
        <button
          onClick={() => setActiveTab('current')}
          className={`pb-3 px-1 font-medium text-sm transition-colors whitespace-nowrap relative ${activeTab === 'current'
            ? 'text-green-600 border-b-2 border-green-600'
            : 'text-gray-500 hover:text-gray-700'
            }`}
        >
          Current Tests
          <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${activeTab === 'current' ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-600'}`}>
            {allTests.filter(t => !isTestOld(t)).length}
          </span>
        </button>
        <button
          onClick={() => setActiveTab('old')}
          className={`pb-3 px-1 font-medium text-sm transition-colors whitespace-nowrap relative ${activeTab === 'old'
            ? 'text-green-600 border-b-2 border-green-600'
            : 'text-gray-500 hover:text-gray-700'
            }`}
        >
          Old/Expired Tests
          <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${activeTab === 'old' ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-600'}`}>
            {allTests.filter(t => isTestOld(t)).length}
          </span>
        </button>
      </div>


      {/* Search and Filters */}
      <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm border border-gray-100 mb-6 space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search tests by name or content..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
        </div>

        <div className="flex flex-col xl:flex-row gap-4 pt-4 border-t border-gray-50">
          {/* Recent Buttons */}
          <div className="grid grid-cols-2 gap-2 w-full xl:w-auto shrink-0">
            <button
              onClick={() => {
                setQuickFilter(quickFilter === 'today' ? null : 'today');
                setDateRange({ from: '', to: '' });
              }}
              className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-colors text-center ${quickFilter === 'today' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >
              Today
            </button>
            <button
              onClick={() => {
                setQuickFilter(quickFilter === 'tomorrow' ? null : 'tomorrow');
                setDateRange({ from: '', to: '' });
              }}
              className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-colors text-center ${quickFilter === 'tomorrow' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >
              Tomorrow
            </button>
          </div>

          <div className="h-px w-full xl:h-auto xl:w-px bg-gray-200 hidden xl:block"></div>

          {/* Date Range Inputs */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 w-full xl:w-auto">
            <div className="flex items-center gap-2 bg-gray-50 p-1.5 rounded-lg border border-gray-200">
              <span className="text-xs font-bold text-gray-500 uppercase px-2">From</span>
              <input
                type="date"
                value={dateRange.from}
                onChange={(e) => {
                  setDateRange(prev => ({ ...prev, from: e.target.value }));
                  setQuickFilter(null);
                }}
                className="w-full bg-transparent text-sm focus:outline-none py-1"
              />
            </div>
            <div className="flex items-center gap-2 bg-gray-50 p-1.5 rounded-lg border border-gray-200">
              <span className="text-xs font-bold text-gray-500 uppercase px-2">To</span>
              <input
                type="date"
                value={dateRange.to}
                onChange={(e) => {
                  setDateRange(prev => ({ ...prev, to: e.target.value }));
                  setQuickFilter(null);
                }}
                className="w-full bg-transparent text-sm focus:outline-none py-1"
              />
            </div>
          </div>

          {/* Clear Filters */}
          {(quickFilter || dateRange.from || dateRange.to) && (
            <button
              onClick={() => {
                setQuickFilter(null);
                setDateRange({ from: '', to: '' });
              }}
              className="xl:ml-auto w-full xl:w-auto px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors font-medium border border-red-100 hover:border-red-200"
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Tests List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="p-4 md:p-6 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">
            {activeTab === 'current' ? 'Current Tests' : 'Old Tests'} ({filteredTests.length})
          </h2>
        </div>

        {loading ? (
          <div className="p-6">
            <SkeletonTable rows={5} columns={8} />
          </div>
        ) : filteredTests.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-gray-200">
                <tr>
                  <th className="text-left py-4 px-6 font-medium text-gray-600 uppercase text-sm">Test Name</th>
                  <th className="text-left py-4 px-6 font-medium text-gray-600 uppercase text-sm">Questions</th>
                  <th className="text-left py-4 px-6 font-medium text-gray-600 uppercase text-sm">Start Date</th>
                  <th className="text-left py-4 px-6 font-medium text-gray-600 uppercase text-sm">End Date</th>
                  <th className="text-left py-4 px-6 font-medium text-gray-600 uppercase text-sm">Duration</th>
                  <th className="text-left py-4 px-6 font-medium text-gray-600 uppercase text-sm">Total Marks</th>
                  <th className="text-left py-4 px-6 font-medium text-gray-600 uppercase text-sm">Add Questions</th>
                  <th className="text-left py-4 px-6 font-medium text-gray-600 uppercase text-sm">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTests.map((test) => (
                  <tr key={test.id} className="border-b border-gray-100 hover:bg-gray-50">
                    {/* Test Name */}
                    <td className="py-4 px-6">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-green-100 rounded-lg">
                          <FileText className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{test.testName}</div>
                          <div className="text-sm text-gray-500">
                            Created: {test.createdAt ? new Date(test.createdAt.toDate ? test.createdAt.toDate() : test.createdAt).toLocaleDateString() : 'N/A'}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Questions Count */}
                    <td className="py-4 px-6 text-left">
                      <span className="font-medium text-blue-600">{test.totalQuestions || test.questionCount || 0}</span>
                    </td>

                    {/* Start Date */}
                    <td className="py-4 px-6 text-left">
                      <div className="text-sm text-gray-600">
                        {test.startDate ? new Date(test.startDate.toDate ? test.startDate.toDate() : test.startDate).toLocaleString('en-GB', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: true
                        }).toUpperCase() : 'N/A'}
                      </div>
                    </td>

                    {/* End Date */}
                    <td className="py-4 px-6 text-left">
                      <div className="text-sm text-gray-600">
                        {test.endDate ? new Date(test.endDate.toDate ? test.endDate.toDate() : test.endDate).toLocaleString('en-GB', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: true
                        }).toUpperCase() : 'N/A'}
                      </div>
                    </td>

                    {/* Duration */}
                    {/* Duration */}
                    <td className="py-4 px-6 text-left">
                      <div className="flex items-center justify-center space-x-1">
                        <Clock className="w-4 h-4 text-green-500" />
                        <span className="font-medium text-green-600">
                          {(() => {
                            try {
                              if (test.startDate && test.endDate) {
                                const start = test.startDate.toDate ? test.startDate.toDate() : new Date(test.startDate);
                                const end = test.endDate.toDate ? test.endDate.toDate() : new Date(test.endDate);
                                const diffMs = end.getTime() - start.getTime();
                                const diffMins = Math.floor(diffMs / 60000);
                                if (!isNaN(diffMins) && diffMins > 0) return `${diffMins} min`;
                              }
                            } catch (e) {
                              // Fallback to stored duration on error
                            }
                            return test.duration ? `${test.duration} min` : '60 min';
                          })()}
                        </span>
                      </div>
                    </td>

                    {/* Total Marks */}
                    <td className="py-4 px-6 text-left">
                      <span className="font-medium text-green-600">
                        {test.totalMarks || (test.totalQuestions ? test.totalQuestions * 4 : (test.questionCount ? test.questionCount * 4 : 0))}
                      </span>
                    </td>

                    {/* Add Questions */}
                    <td className="py-4 px-6 text-left">
                      <button
                        onClick={() => navigate(`/add-questions/${test.id}?testName=${encodeURIComponent(test.testName)}&from=online`)}
                        className="p-2 bg-green-100 text-green-600 rounded-full hover:bg-green-200 transition-colors"
                        title="Add Questions"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </td>

                    {/* Actions */}
                    <td className="py-4 px-6">
                      <div className="flex items-center justify-center space-x-2">
                        <button
                          onClick={() => navigate(`/online-test-questions/${test.id}?testName=${encodeURIComponent(test.testName)}&from=online`)}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                          title="View Questions"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => navigate(`/online-test-edit/${test.id}`)}
                          className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                          title="Edit Test"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteTest(test)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                          title="Delete Test"
                          disabled={loading}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-12 text-center">
            <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <FileText className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No tests found</h3>
            <p className="text-gray-600 mb-6">
              {searchTerm
                ? `No tests match "${searchTerm}".Try a different search term.`
                : 'Get started by creating your first online test'
              }
            </p>
            <button
              onClick={handleCreateNewTest}
              className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors inline-flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Create First Test
            </button>
          </div>
        )}
      </div>

      {/* Create Test Form Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-green-600 to-green-700 px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-bold text-white">Create New Online Test</h2>
              <button
                onClick={handleCloseForm}
                className="text-white hover:text-gray-200 transition-colors"
                aria-label="Close"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body - Scrollable */}
            <div className="overflow-y-auto max-h-[calc(90vh-140px)]">
              <form onSubmit={handleFormSubmit} className="p-6 space-y-5">
                {/* Test Name */}
                <div>
                  <label htmlFor="testName" className="block text-sm font-semibold text-gray-700 mb-2">
                    Test Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="testName"
                    name="testName"
                    value={formData.testName}
                    onChange={handleInputChange}
                    placeholder="Enter test name"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                    required
                  />
                </div>

                {/* Date Range */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Start Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      id="startDate"
                      name="startDate"
                      value={formData.startDate}
                      onChange={handleInputChange}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      End Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      id="endDate"
                      name="endDate"
                      value={formData.endDate}
                      onChange={handleInputChange}
                      min={formData.startDate || new Date().toISOString().split('T')[0]}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                      required
                    />
                  </div>
                </div>

                {/* Time Range */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Test Start Time */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      <Clock className="w-4 h-4 inline mr-1 text-green-600" />
                      Test Start Time <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-2">
                      <select
                        name="testStartHour"
                        value={formData.testStartHour}
                        onChange={handleInputChange}
                        className="flex-1 px-2 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 font-semibold text-center"
                        required
                      >
                        {hours.map(h => <option key={h} value={h}>{h}</option>)}
                      </select>
                      <span className="self-center font-bold text-gray-400">:</span>
                      <select
                        name="testStartMinute"
                        value={formData.testStartMinute}
                        onChange={handleInputChange}
                        className="flex-1 px-2 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 font-semibold text-center"
                        required
                      >
                        {minutes.map(m => <option key={m} value={m}>{m}</option>)}
                      </select>
                      <select
                        name="testStartPeriod"
                        value={formData.testStartPeriod}
                        onChange={handleInputChange}
                        className="w-24 px-2 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 font-semibold text-center"
                        required
                      >
                        <option value="AM">AM</option>
                        <option value="PM">PM</option>
                      </select>
                    </div>
                    <p className="text-xs text-gray-500 mt-1.5">When students can start</p>
                  </div>

                  {/* Test Close Time */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      <Clock className="w-4 h-4 inline mr-1 text-red-600" />
                      Test Close Time <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-2">
                      <select
                        name="testCloseHour"
                        value={formData.testCloseHour}
                        onChange={handleInputChange}
                        className="flex-1 px-2 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 font-semibold text-center"
                        required
                      >
                        {hours.map(h => <option key={h} value={h}>{h}</option>)}
                      </select>
                      <span className="self-center font-bold text-gray-400">:</span>
                      <select
                        name="testCloseMinute"
                        value={formData.testCloseMinute}
                        onChange={handleInputChange}
                        className="flex-1 px-2 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 font-semibold text-center"
                        required
                      >
                        {minutes.map(m => <option key={m} value={m}>{m}</option>)}
                      </select>
                      <select
                        name="testClosePeriod"
                        value={formData.testClosePeriod}
                        onChange={handleInputChange}
                        className="w-24 px-2 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 font-semibold text-center"
                        required
                      >
                        <option value="AM">AM</option>
                        <option value="PM">PM</option>
                      </select>
                    </div>
                    <p className="text-xs text-gray-500 mt-1.5">After this, test closes</p>
                  </div>
                </div>

                {/* Duration and Marks */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="duration" className="block text-sm font-semibold text-gray-700 mb-2">
                      Test Duration (minutes) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      id="duration"
                      name="duration"
                      value={formData.duration}
                      onChange={handleInputChange}
                      placeholder="e.g., 30, 60, 90"
                      min="1"
                      max="500"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1.5">Enter duration in minutes</p>
                  </div>

                  <div>
                    <label htmlFor="totalMarks" className="block text-sm font-semibold text-gray-700 mb-2">
                      Total Marks
                    </label>
                    <input
                      type="text"
                      id="totalMarks"
                      name="totalMarks"
                      value={formData.totalMarks}
                      onChange={handleInputChange}
                      placeholder="e.g., 20 or leave empty"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                    />
                  </div>
                </div>
              </form>
            </div>

            {/* Modal Footer */}
            <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex gap-3 justify-end">
              <button
                type="button"
                onClick={handleCloseForm}
                className="px-6 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                onClick={handleFormSubmit}
                disabled={loading}
                className="px-6 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:bg-green-400 disabled:cursor-not-allowed font-medium shadow-sm"
              >
                {loading ? 'Creating...' : 'Create Test'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OnlineTestManagement;
